<template>
  <div class="card-management-table">
    <!-- 卡密列表 -->
    <section class="cards-section">
      <h3 v-if="sectionTitle">{{ sectionTitle }}</h3>

      <!-- 操作按钮 + 搜索框同一行 -->
      <div class="toolbar flex gap-sm align-items-center flex-wrap">
        <input
          type="text"
          v-model="searchKeyword"
          placeholder="搜索卡密代码或邮箱"
          @keyup.enter="filterCards"
          class="form-control"
          style="flex: 1; max-width: 300px;"
        />
        <button @click="filterCards" class="btn btn-primary">
          搜索
        </button>
        <button @click="clearSearch" class="btn btn-secondary">
          清空
        </button>
        <template v-if="!hideActions">
          <button @click="showCreateDialog = true" class="btn btn-primary" style="margin-left: auto;">
            {{ createButtonText }}
          </button>
          <button @click="showBatchInputDialog = true" class="btn btn-info">
            批量录入
          </button>
          <button @click="exportCards" class="btn btn-warning" v-if="!hideExport">
            导出卡密
          </button>
          <button @click="loadCards" :disabled="loading" class="btn btn-success">
            刷新
          </button>
        </template>
      </div>

      <!-- 移动端卡片式布局 -->
      <div class="mobile-cards" v-if="isMobile">
        <div v-if="loading" class="mobile-loading">
          正在加载卡密数据...
        </div>
        <div v-else-if="cards.length === 0" class="mobile-empty">
          <p v-if="totalCards === 0">暂无卡密数据</p>
          <p v-else>第 {{ currentPage }} 页暂无数据</p>
        </div>
        <div v-else class="mobile-card-list">
          <div v-for="item in cards" :key="item.id" class="mobile-card-item">
            <div class="mobile-card-header">
              <code class="card-code">{{ item.cardCode }}</code>
              <span class="status-badge" :class="getStatusClass(item.status)">
                {{ getStatusText(item.status) }}
              </span>
            </div>
            <div class="mobile-card-content">
              <div class="mobile-card-row">
                <span class="label">类型:</span>
                <span class="type-badge" :class="getCardTypeClass(item.cardType)">
                  {{ getCardTypeText(item.cardType) }}
                </span>
              </div>
              <div class="mobile-card-row">
                <span class="label">会员类型:</span>
                <span>{{ getTypeName(item.typeId) }}</span>
              </div>
              <div class="mobile-card-row" v-if="item.boundEmail">
                <span class="label">绑定邮箱:</span>
                <span>{{ item.boundEmail }}</span>
              </div>
              <div class="mobile-card-row" v-if="item.firstActivatedTime">
                <span class="label">首次激活:</span>
                <span>{{ formatDate(item.firstActivatedTime) }}</span>
              </div>
              <div class="mobile-card-row" v-if="item.expireTime">
                <span class="label">到期时间:</span>
                <span>{{ formatDate(item.expireTime) }}</span>
              </div>
            </div>
            <div class="mobile-card-actions" v-if="!readonly">
              <button @click="editCard(item)" class="btn btn-warning btn-sm">
                编辑
              </button>
              <button @click="deleteCard(item.id)" class="btn btn-danger btn-sm">
                删除
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 桌面端表格布局 -->
      <AdminTable
        v-else
        :columns="tableColumns"
        :data="cards"
        :total="totalCards"
        :current-page="currentPage"
        :page-size="pageSize"
        :row-key="item => item.id"
        :loading="loading"
        max-height="600px"
      >
        <template #empty>
          <div class="empty-state">
            <p v-if="loading">正在加载卡密数据...</p>
            <p v-else-if="totalCards === 0">暂无卡密数据</p>
            <p v-else>第 {{ currentPage }} 页暂无数据</p>
          </div>
        </template>

        <template #cardCode="{ item }">
          <code class="card-code">{{ item.cardCode }}</code>
        </template>

        <template #cardType="{ item }">
          <span class="type-badge" :class="getCardTypeClass(item.cardType)">
            {{ getCardTypeText(item.cardType) }}
          </span>
        </template>

        <template #type="{ item }">
          {{ getTypeName(item.typeId) }}
        </template>

        <template #boundEmail="{ item }">
          {{ item.boundEmail || '-' }}
        </template>

        <template #status="{ item }">
          <span class="status-badge" :class="getStatusClass(item.status)">
            {{ getStatusText(item.status) }}
          </span>
        </template>

        <template #firstActivatedTime="{ item }">
          {{ formatDate(item.firstActivatedTime) }}
        </template>

        <template #expireTime="{ item }">
          {{ formatDate(item.expireTime) }}
        </template>

        <template #createTime="{ item }">
          {{ formatDate(item.createTime) }}
        </template>

        <template #actions="{ item }" v-if="!readonly">
          <div class="flex gap-sm">
            <button @click="editCard(item)" class="btn btn-warning btn-sm">
              编辑
            </button>
            <button @click="deleteCard(item.id)" class="btn btn-danger btn-sm">
              删除
            </button>
          </div>
        </template>
      </AdminTable>

      <AdminPagination
        :current-page="currentPage"
        :page-size="pageSize"
        :total="totalCards"
        :show-total="true"
        :show-sizer="true"
        :show-jumper="false"
        @update:currentPage="currentPage = $event; loadCards()"
        @update:pageSize="pageSize = $event; currentPage = 1; loadCards()"
      />
    </section>

    <!-- 创建卡密对话框 -->
    <AdminModal
      :visible="showCreateDialog"
      :title="createDialogTitle"
      @close="closeCreateDialog"
    >
      <form @submit.prevent="createCard">
        <div class="form-group">
          <label>卡密代码</label>
          <input type="text" v-model="newCard.cardCode" class="form-control" required />
        </div>
        <div class="form-group" v-if="showMemberTypeSelect">
          <label>会员类型</label>
          <select v-model="newCard.typeId" class="form-control" required>
            <option v-for="(name, id) in typeNames" :key="id" :value="id">
              {{ name }}
            </option>
          </select>
        </div>
        <div class="form-group" v-if="showCardTypeSelect">
          <label>卡密类型</label>
          <select v-model="newCard.cardType" class="form-control" required>
            <option value="member">会员激活卡密（可重复导出）</option>
            <option value="export">导出专用卡密（一次性）</option>
          </select>
        </div>
        <div class="form-group" v-if="showBatchCount">
          <label>数量</label>
          <input
            type="number"
            v-model="newCard.count"
            class="form-control"
            min="1"
            max="100"
            placeholder="批量创建数量(1-100)"
          />
        </div>
      </form>

      <template #footer>
        <button type="button" @click="closeCreateDialog" class="btn btn-secondary">
          取消
        </button>
        <button @click="createCard" :disabled="creating" class="btn btn-primary">
          {{ creating ? '创建中...' : '创建' }}
        </button>
      </template>
    </AdminModal>

    <!-- 编辑卡密对话框 -->
    <AdminModal
      :visible="showEditDialog"
      title="编辑卡密"
      size="md"
      @close="closeEditDialog"
      v-if="!readonly"
    >
      <form @submit.prevent="updateCard">
        <div class="form-group">
          <label>卡密代码</label>
          <input type="text" v-model="editingCard.cardCode" class="form-control" required />
        </div>
        <div class="form-group" v-if="showMemberTypeSelect">
          <label>会员类型</label>
          <select v-model="editingCard.typeId" class="form-control" required>
            <option v-for="(name, id) in typeNames" :key="id" :value="id">
              {{ name }}
            </option>
          </select>
        </div>
        <div class="form-group" v-if="showCardTypeSelect">
          <label>卡密类型</label>
          <select v-model="editingCard.cardType" class="form-control" required>
            <option value="member">会员激活卡密（可重复导出）</option>
            <option value="export">导出专用卡密（一次性）</option>
          </select>
        </div>
        <div class="form-group">
          <label>绑定邮箱</label>
          <input type="email" v-model="editingCard.boundEmail" class="form-control" placeholder="已绑定的用户邮箱（可修改）" />
          <small class="form-hint">修改后将解除与原邮箱的绑定关系</small>
        </div>
        <div class="form-group">
          <label>卡密状态</label>
          <select v-model="editingCard.status" class="form-control">
            <option value="0">未激活 - 可正常使用</option>
            <option value="1">已激活 - 已被用户使用</option>
            <option value="2">已过期 - 无效不可用</option>
          </select>
          <small class="form-hint">选择"已过期"可让卡密立即失效</small>
        </div>
        <div class="form-group" v-if="editingCard.status == 1">
          <label>到期时间</label>
          <input type="datetime-local" v-model="editingCard.expireTime" class="form-control" />
        </div>
      </form>

      <template #footer>
        <button type="button" @click="closeEditDialog" class="btn btn-secondary">
          取消
        </button>
        <button @click="updateCard" :disabled="updating" class="btn btn-primary">
          {{ updating ? '更新中...' : '更新' }}
        </button>
      </template>
    </AdminModal>

    <!-- 批量录入对话框 -->
    <AdminModal
      :visible="showBatchInputDialog"
      title="批量录入卡密"
      size="lg"
      @close="closeBatchInputDialog"
    >
      <div class="batch-input-tabs">
        <button
          @click="batchInputMode = 'text'"
          :class="['tab-button', { active: batchInputMode === 'text' }]"
        >
          文本录入
        </button>
        <button
          @click="batchInputMode = 'file'"
          :class="['tab-button', { active: batchInputMode === 'file' }]"
        >
          文件上传
        </button>
      </div>

      <form @submit.prevent="batchInputCards">
        <div class="form-group" v-if="showMemberTypeSelect">
          <label>会员类型</label>
          <select v-model="batchInputForm.typeId" class="form-control" required>
            <option v-for="(name, id) in typeNames" :key="id" :value="id">
              {{ name }}
            </option>
          </select>
        </div>

        <div class="form-group" v-if="showCardTypeSelect">
          <label>卡密类型</label>
          <select v-model="batchInputForm.cardType" class="form-control" required>
            <option value="member">会员激活卡密（可重复导出）</option>
            <option value="export">导出专用卡密（一次性）</option>
          </select>
        </div>

        <div v-if="batchInputMode === 'text'" class="form-group">
          <label>卡密列表 (每行一个卡密)</label>
          <textarea
            v-model="batchInputForm.cardsText"
            placeholder="输入卡密，每行一个：&#10;CARD001&#10;CARD002&#10;CARD003"
            rows="10"
            class="form-control"
            required
          ></textarea>
          <small class="form-hint">每行输入一个卡密代码</small>
        </div>

        <div v-if="batchInputMode === 'file'" class="form-group">
          <label>上传文件 (支持 .txt, .csv)</label>
          <input
            type="file"
            @change="handleFileUpload"
            accept=".txt,.csv"
            class="form-control"
            required
          />
          <small class="form-hint">文件格式：每行一个卡密代码或CSV格式</small>
          <div v-if="filePreview" class="file-preview">
            <p>预览 (前10行):</p>
            <pre>{{ filePreview }}</pre>
          </div>
        </div>
      </form>

      <template #footer>
        <button type="button" @click="closeBatchInputDialog" class="btn btn-secondary">
          取消
        </button>
        <button @click="batchInputCards" :disabled="batchInputCreating" class="btn btn-primary">
          {{ batchInputCreating ? '录入中...' : '批量录入' }}
        </button>
      </template>
    </AdminModal>

    <!-- 导出卡密对话框 -->
    <AdminModal
      :visible="showExportDialog"
      title="导出卡密"
      @close="closeExportDialog"
      v-if="!hideExport"
    >
      <div class="export-options">
        <div class="form-group">
          <label>导出方式</label>
          <select v-model="exportForm.method" class="form-control">
            <option value="unused">导出未使用的卡密</option>
            <option value="type">按类型导出</option>
            <option value="count">导出指定数量</option>
          </select>
        </div>

        <div v-if="exportForm.method === 'type'" class="form-group">
          <label>会员类型</label>
          <select v-model="exportForm.typeId" class="form-control">
            <option v-for="(name, id) in typeNames" :key="id" :value="id">
              {{ name }}
            </option>
          </select>
        </div>

        <div v-if="exportForm.method === 'count'" class="form-group">
          <label>导出数量</label>
          <input type="number" v-model="exportForm.count" min="1" max="1000" class="form-control" />
        </div>

        <div class="form-group">
          <label>输出格式</label>
          <select v-model="exportForm.format" class="form-control">
            <option value="text">纯文本</option>
            <option value="csv">CSV格式</option>
            <option value="json">JSON格式</option>
          </select>
        </div>
      </div>

      <template #footer>
        <button type="button" @click="closeExportDialog" class="btn btn-secondary">
          取消
        </button>
        <button @click="executeExport" :disabled="exporting" class="btn btn-primary">
          {{ exporting ? '导出中...' : '导出' }}
        </button>
      </template>
    </AdminModal>

    <div v-if="message" class="message" :class="messageType">
      {{ message }}
    </div>
  </div>
</template>

<script>
import AdminTable from './AdminTable.vue'
import AdminPagination from './AdminPagination.vue'
import AdminModal from './AdminModal.vue'
import {
  getCardList,
  createCard as createCardAPI,
  updateCard,
  deleteCard as deleteCardAPI,
  batchInputCards as batchInputCardsAPI,
  pushCards as pushCardsAPI
} from '@/api/cards'

export default {
  name: 'CardManagementTable',
  components: {
    AdminTable,
    AdminPagination,
    AdminModal
  },
  props: {
    // 筛选的卡密类型 ('member', 'export', null)
    cardTypeFilter: {
      type: String,
      default: null
    },
    // 是否隐藏操作按钮
    hideActions: {
      type: Boolean,
      default: false
    },
    // 是否隐藏导出功能
    hideExport: {
      type: Boolean,
      default: false
    },
    // 是否只读模式（不可编辑删除）
    readonly: {
      type: Boolean,
      default: false
    },
    // 自定义区块标题
    sectionTitle: {
      type: String,
      default: ''
    },
    // 是否显示卡密类型选择
    showCardTypeSelect: {
      type: Boolean,
      default: true
    },
    // 是否显示批量创建数量
    showBatchCount: {
      type: Boolean,
      default: false
    },
    // 创建按钮文字
    createButtonText: {
      type: String,
      default: '创建卡密'
    },
    // 创建对话框标题
    createDialogTitle: {
      type: String,
      default: '创建卡密'
    },
    // 是否显示会员类型选择器
    showMemberTypeSelect: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      cards: [],
      totalCards: 0,
      searchKeyword: '',
      loading: false,
      creating: false,
      updating: false,
      batchInputCreating: false,
      exporting: false,
      showCreateDialog: false,
      showEditDialog: false,
      showBatchInputDialog: false,
      showExportDialog: false,
      batchInputMode: 'text',
      filePreview: '',
      isMobile: false,
      currentPage: 1,
      pageSize: 20,
      newCard: {
        cardCode: '',
        typeId: '2207032',
        cardType: 'member',
        count: 1
      },
      editingCard: {
        id: null,
        cardCode: '',
        typeId: '2207032',
        cardType: 'member',
        boundEmail: '',
        status: 0,
        expireTime: ''
      },
      batchInputForm: {
        typeId: '2207032',
        cardType: 'member',
        cardsText: '',
        cardsList: []
      },
      exportForm: {
        method: 'unused',
        typeId: '2207032',
        count: 100,
        format: 'text'
      },
      message: '',
      messageType: 'success',
      typeNames: {
        2207001: '15分钟',
        2207002: '1小时',
        2207003: '24小时',
        2207030: '14天',
        2207032: '1个月',
        2207033: '2个月',
        2207034: '3个月',
        2207038: '4个月',
        2207039: '5个月',
        2207035: '6个月',
        2207040: '7个月',
        2207041: '8个月',
        2207042: '9个月',
        2207043: '10个月',
        2207044: '11个月',
        2207036: '1年',
        2207037: '长期'
      }
    }
  },
  computed: {
    tableColumns() {
      const baseColumns = [
        { key: 'id', title: 'ID', width: '80px' },
        { key: 'cardCode', title: '卡密代码' }
      ]

      // 根据是否显示卡密类型选择决定是否显示类型列
      if (this.showCardTypeSelect) {
        baseColumns.push({ key: 'cardType', title: '卡密类型' })
      }

      baseColumns.push(
        { key: 'type', title: '会员类型' },
        { key: 'boundEmail', title: '绑定邮箱' },
        { key: 'status', title: '状态' },
        { key: 'firstActivatedTime', title: '首次激活时间' },
        { key: 'expireTime', title: '到期时间' },
        { key: 'createTime', title: '创建时间' }
      )

      if (!this.readonly) {
        baseColumns.push({ key: 'actions', title: '操作' })
      }

      return baseColumns
    }
  },
  mounted() {
    this.loadCards()
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile)
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 768
    },

    async loadCards() {
      this.loading = true
      try {
        const params = {
          current: this.currentPage,
          size: this.pageSize
        }

        // 添加搜索参数
        if (this.searchKeyword.trim()) {
          params.keyword = this.searchKeyword.trim()
        }

        // 添加卡密类型筛选
        if (this.cardTypeFilter) {
          params.cardType = this.cardTypeFilter
        }

        const response = await getCardList(params)
        if (response.code === 200) {
          if (response.data?.cards !== undefined) {
            this.cards = response.data.cards || []
            this.totalCards = response.data.total || 0
          } else if (response.data?.records) {
            this.cards = response.data.records || []
            this.totalCards = response.data.total || 0
          } else {
            this.cards = response.data || []
            this.totalCards = this.cards.length
          }
        } else {
          this.showMessage(response.message || '加载卡密列表失败', 'error')
          this.cards = []
          this.totalCards = 0
        }
      } catch (error) {
        this.showMessage('加载卡密列表失败: ' + error.message, 'error')
        this.cards = []
        this.totalCards = 0
      } finally {
        this.loading = false
      }
    },

    async createCard() {
      this.creating = true
      try {
        // 如果显示批量数量且数量大于1，使用批量创建
        if (this.showBatchCount && this.newCard.count > 1) {
          const cardsList = []
          const baseCode = this.newCard.cardCode
          const count = parseInt(this.newCard.count)

          for (let i = 1; i <= count; i++) {
            cardsList.push(`${baseCode}_${i.toString().padStart(3, '0')}`)
          }

          const response = await batchInputCardsAPI({
            typeId: parseInt(this.newCard.typeId),
            cardType: this.newCard.cardType,
            cardsList: cardsList
          })

          if (response.code === 200) {
            this.showMessage(`卡密批量创建成功，共创建 ${count} 个`, 'success')
            this.closeCreateDialog()
            this.loadCards()
          } else {
            this.showMessage(response.message || '批量创建卡密失败', 'error')
          }
        } else {
          // 单个创建
          const response = await createCardAPI({
            ...this.newCard,
            typeId: parseInt(this.newCard.typeId)
          })
          if (response.code === 200) {
            this.showMessage('卡密创建成功', 'success')
            this.closeCreateDialog()
            this.loadCards()
          } else {
            this.showMessage(response.message || '创建卡密失败', 'error')
          }
        }
      } catch (error) {
        this.showMessage('创建卡密失败: ' + error.message, 'error')
      } finally {
        this.creating = false
      }
    },

    async batchInputCards() {
      this.batchInputCreating = true
      try {
        let cardsList = []

        if (this.batchInputMode === 'text') {
          cardsList = this.batchInputForm.cardsText
            .split('\n')
            .map(line => line.trim())
            .filter(line => line.length > 0)
        } else {
          cardsList = this.batchInputForm.cardsList
        }

        if (cardsList.length === 0) {
          this.showMessage('请提供要录入的卡密列表', 'error')
          return
        }

        const data = {
          typeId: parseInt(this.batchInputForm.typeId),
          cardType: this.batchInputForm.cardType,
          cardsList: cardsList
        }

        const response = await batchInputCardsAPI(data)
        if (response.code === 200) {
          this.showMessage(`批量录入成功，共录入 ${cardsList.length} 个卡密`, 'success')
          this.closeBatchInputDialog()
          this.loadCards()
        } else {
          this.showMessage(response.message || '批量录入卡密失败', 'error')
        }
      } catch (error) {
        this.showMessage('批量录入卡密失败: ' + error.message, 'error')
      } finally {
        this.batchInputCreating = false
      }
    },

    handleFileUpload(event) {
      const file = event.target.files[0]
      if (!file) return

      const reader = new FileReader()
      reader.onload = (e) => {
        const content = e.target.result
        let cardsList = []

        if (file.name.endsWith('.csv')) {
          const lines = content.split('\n')
          lines.forEach(line => {
            const columns = line.split(',')
            if (columns.length > 0 && columns[0].trim()) {
              cardsList.push(columns[0].trim())
            }
          })
        } else {
          cardsList = content
            .split('\n')
            .map(line => line.trim())
            .filter(line => line.length > 0)
        }

        this.batchInputForm.cardsList = cardsList
        this.filePreview = cardsList.slice(0, 10).join('\n')
        if (cardsList.length > 10) {
          this.filePreview += '\n...(还有 ' + (cardsList.length - 10) + ' 行)'
        }
      }
      reader.readAsText(file)
    },

    exportCards() {
      this.showExportDialog = true
    },

    async executeExport() {
      this.exporting = true
      try {
        const response = await pushCardsAPI(this.exportForm)
        if (response.code === 200) {
          const data = response.data
          this.downloadData(data, this.exportForm.format)
          this.showMessage('卡密导出成功', 'success')
          this.closeExportDialog()
        } else {
          this.showMessage(response.message || '导出卡密失败', 'error')
        }
      } catch (error) {
        this.showMessage('导出卡密失败: ' + error.message, 'error')
      } finally {
        this.exporting = false
      }
    },

    downloadData(data, format) {
      let content = ''
      let fileName = 'cards'

      if (format === 'json') {
        content = JSON.stringify(data, null, 2)
        fileName += '.json'
      } else if (format === 'csv') {
        if (data.length > 0) {
          const headers = Object.keys(data[0]).join(',')
          const rows = data.map(item => Object.values(item).join(','))
          content = headers + '\n' + rows.join('\n')
        }
        fileName += '.csv'
      } else {
        content = data.map(item => item.cardCode || item).join('\n')
        fileName += '.txt'
      }

      const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName
      link.click()
      window.URL.revokeObjectURL(url)
    },

    async deleteCard(id) {
      if (!confirm('确定要删除这个卡密吗？')) return

      try {
        const response = await deleteCardAPI(id)
        if (response.code === 200) {
          this.showMessage('卡密删除成功', 'success')
          this.loadCards()
        } else {
          this.showMessage(response.message || '删除卡密失败', 'error')
        }
      } catch (error) {
        this.showMessage('删除卡密失败: ' + error.message, 'error')
      }
    },

    filterCards() {
      this.currentPage = 1
      this.loadCards()
    },

    clearSearch() {
      this.searchKeyword = ''
      this.currentPage = 1
      this.loadCards()
    },

    getTypeName(typeId) {
      return this.typeNames[typeId] || '未知'
    },

    getCardTypeText(cardType) {
      switch (cardType) {
        case 'export': return '导出专用'
        case 'member': return '会员激活'
        default: return '会员激活' // 默认为会员类型，兼容旧数据
      }
    },

    getCardTypeClass(cardType) {
      switch (cardType) {
        case 'export': return 'export-type'
        case 'member': return 'member-type'
        default: return 'member-type'
      }
    },

    getStatusText(status) {
      switch (status) {
        case 0: return '未激活'
        case 1: return '已激活'
        case 2: return '已过期'
        default: return '未知'
      }
    },

    getStatusClass(status) {
      switch (status) {
        case 0: return 'status-inactive'
        case 1: return 'status-active'
        case 2: return 'status-expired'
        default: return ''
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleString('zh-CN')
    },

    closeCreateDialog() {
      this.showCreateDialog = false
      this.newCard = {
        cardCode: '',
        typeId: this.showMemberTypeSelect ? '2207032' : '2207032', // 默认1个月，导出卡密也使用此类型
        cardType: this.cardTypeFilter || 'member',
        count: 1
      }
    },

    editCard(card) {
      this.editingCard = {
        id: card.id,
        cardCode: card.cardCode,
        typeId: card.typeId.toString(),
        cardType: card.cardType || 'member',
        boundEmail: card.boundEmail || '',
        status: card.status.toString(),
        expireTime: card.expireTime ? this.formatDateTimeLocal(card.expireTime) : ''
      }
      this.showEditDialog = true
    },

    async updateCard() {
      this.updating = true
      try {
        const response = await updateCard(this.editingCard.id, {
          ...this.editingCard,
          typeId: parseInt(this.editingCard.typeId),
          status: parseInt(this.editingCard.status)
        })
        if (response.code === 200) {
          this.showMessage('卡密更新成功', 'success')
          this.closeEditDialog()
          this.loadCards()
        } else {
          this.showMessage(response.message || '更新卡密失败', 'error')
        }
      } catch (error) {
        this.showMessage('更新卡密失败: ' + error.message, 'error')
      } finally {
        this.updating = false
      }
    },

    formatDateTimeLocal(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day}T${hours}:${minutes}`
    },

    closeEditDialog() {
      this.showEditDialog = false
      this.editingCard = {
        id: null,
        cardCode: '',
        typeId: '2207032',
        cardType: 'member',
        boundEmail: '',
        status: 0,
        expireTime: ''
      }
    },

    closeBatchInputDialog() {
      this.showBatchInputDialog = false
      this.batchInputForm = {
        typeId: this.showMemberTypeSelect ? '2207032' : '2207032', // 默认1个月，导出卡密也使用此类型
        cardType: this.cardTypeFilter || 'member',
        cardsText: '',
        cardsList: []
      }
      this.filePreview = ''
    },

    closeExportDialog() {
      this.showExportDialog = false
      this.exportForm = { method: 'unused', typeId: '2207032', count: 100, format: 'text' }
    },

    showMessage(text, type = 'success') {
      this.message = text
      this.messageType = type
      setTimeout(() => {
        this.message = ''
      }, 3000)
      // 向父组件发出消息事件
      this.$emit('message', { text, type })
    }
  },
  watch: {
    cardTypeFilter: {
      immediate: true,
      handler(newVal) {
        // 当筛选类型改变时，重新设置默认值
        this.newCard.cardType = newVal || 'member'
        this.batchInputForm.cardType = newVal || 'member'
        if (this.cards.length > 0) {
          this.loadCards()
        }
      }
    }
  }
}
</script>

<style scoped>
@import '@/styles/admin-common.css';

.cards-section {
  margin-bottom: var(--spacing-xxl);
}

.toolbar {
  margin-bottom: var(--spacing-xl);
}

@media (max-width: 768px) {
  .toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .toolbar input.form-control {
    max-width: none !important;
    margin-left: 0 !important;
  }

  .toolbar .btn {
    width: 100%;
  }
}

.card-code {
  background: var(--bg-primary);
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--border-radius-sm);
  font-family: monospace;
  font-size: var(--font-size-sm);
  color: var(--primary-color);
  border: 1px solid var(--border-light);
}

.type-badge {
  padding: var(--spacing-xxs) var(--spacing-sm);
  border-radius: var(--border-radius-sm);
  font-size: var(--font-size-xs);
  font-weight: 600;
  text-transform: uppercase;
}

.export-type {
  background: #e3f2fd;
  color: #1976d2;
  border: 1px solid #bbdefb;
}

.member-type {
  background: #f3e5f5;
  color: #7b1fa2;
  border: 1px solid #ce93d8;
}

.batch-input-tabs {
  display: flex;
  margin-bottom: var(--spacing-xl);
  border-bottom: 1px solid var(--border-light);
}

.tab-button {
  padding: var(--spacing-sm) var(--spacing-xl);
  border: none;
  background: none;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  font-weight: 500;
  color: var(--text-secondary);
  transition: all var(--transition-fast);
}

.tab-button.active {
  color: var(--primary-color);
  border-bottom-color: var(--primary-color);
}

.tab-button:hover {
  color: var(--primary-color);
}

.file-preview {
  margin-top: var(--spacing-sm);
  padding: var(--spacing-sm);
  background-color: var(--bg-primary);
  border: 1px solid var(--border-light);
  border-radius: var(--border-radius-sm);
}

.file-preview pre {
  margin: 0;
  font-size: var(--font-size-sm);
  max-height: 150px;
  overflow-y: auto;
  color: var(--text-secondary);
}

.export-options {
  margin-bottom: var(--spacing-xl);
}

.empty-state {
  text-align: center;
  padding: var(--spacing-xl);
  color: var(--text-secondary);
}

.empty-state p {
  margin: 0;
  font-style: italic;
}

/* 移动端卡片样式 */
.mobile-cards {
  display: block;
}

.mobile-loading, .mobile-empty {
  text-align: center;
  padding: var(--spacing-xl);
  color: var(--text-secondary);
  font-style: italic;
}

.mobile-card-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.mobile-card-item {
  background: var(--bg-primary);
  border: 1px solid var(--border-light);
  border-radius: var(--border-radius-md);
  padding: var(--spacing-md);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.mobile-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-sm);
  padding-bottom: var(--spacing-sm);
  border-bottom: 1px solid var(--border-light);
}

.mobile-card-content {
  margin-bottom: var(--spacing-md);
}

.mobile-card-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-xs) 0;
  border-bottom: 1px solid var(--border-lighter);
}

.mobile-card-row:last-child {
  border-bottom: none;
}

.mobile-card-row .label {
  font-weight: 600;
  color: var(--text-secondary);
  flex: 0 0 auto;
  min-width: 100px;
}

.mobile-card-actions {
  display: flex;
  gap: var(--spacing-sm);
  justify-content: flex-end;
}

.mobile-card-actions .btn {
  flex: 1;
  max-width: 100px;
}
</style>